/**
 * components/shared/AppLayout.js
 * 
 * Main application shell:
 * - Sidebar navigation (desktop)
 * - Top nav bar (mobile)
 * - Outlet for page content
 */

import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AppLayout.css';

// Nav items config
const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: '⬡' },
  { path: '/workouts', label: 'Workouts', icon: '◈' },
  { path: '/sleep', label: 'Sleep', icon: '◎' },
  { path: '/calendar', label: 'Calendar', icon: '◻' },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // User initials for avatar
  const initials = user?.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  return (
    <div className="app-layout">
      {/* ─── Sidebar (desktop) ─── */}
      <aside className={`sidebar ${mobileOpen ? 'sidebar-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="logo-mark">⬡</div>
          <span className="logo-text">LifeOS</span>
          <button
            className="sidebar-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >✕</button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section at bottom */}
        <div className="sidebar-user">
          <div className="user-avatar" style={{ background: user?.avatar_color }}>
            {initials}
          </div>
          <div className="user-info">
            <div className="user-name">{user?.name}</div>
            <div className="user-email">{user?.email}</div>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            ⎋
          </button>
        </div>
      </aside>

      {/* ─── Mobile top bar ─── */}
      <header className="mobile-header">
        <button
          className="menu-btn"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          ☰
        </button>
        <span className="mobile-logo">LifeOS</span>
        <div className="user-avatar-sm" style={{ background: user?.avatar_color }}>
          {initials}
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ─── Main Content Area ─── */}
      <main className="main-content">
        <div className="page-content animate-fadein">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
